import { cn } from '@/lib/utils';
import { Button } from '@/shared/ui/button.tsx';
import { Calendar } from '@/shared/ui/calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover.tsx';
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/toggle-group.tsx';
import { format } from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import { SetStateAction } from 'react';
import { DateRange } from 'react-day-picker';
import { SuggestedPostFilterType } from '../UnreviewedTab';
import { Checkbox } from '@/shared/ui/checkbox.tsx';

type Props = {
  filter: SuggestedPostFilterType,
  setFilter: React.Dispatch<SetStateAction<SuggestedPostFilterType>>,
  date: DateRange | undefined,
  setDateFilter: React.Dispatch<SetStateAction<DateRange | undefined>>,
  categories: string[]
}

const EventFilter = ({ filter, setFilter, categories, date, setDateFilter }: Props) => {
  return (
    <div id="filters" className="lg:fixed left-20 top-[20%] w-full lg:w-[25%] 2xl:w-[17%] size-full ps-0 md:p-4">
      <div className='flex flex-col gap-5 h-fit p-4'>
        <div className='flex items-center justify-between'>
          <h3 className='text-2xl font-montserrat'>Filters</h3>
          <Button
            size="icon"
            variant="secondary"
            onClick={() => setFilter({ name: "", startDate: undefined, endDate: undefined, type: undefined, event_category: [], isFilter: false })}
          ><X /></Button>
        </div>
        <div className='flex flex-col gap-2'>
          <p className='font-poppins font-light'>Event Type</p>
          <ToggleGroup type="single" value={filter.type} className='flex-col items-start w-full'
            onValueChange={(value: string) => setFilter(prev => ({ ...prev, type: value }))}>
            <ToggleGroupItem value="Post" aria-label="Toggle Post" className='w-2/3 checkbox font-normal data-[state=on]:font-semibold text-start justify-start font-poppins'>
              Post
            </ToggleGroupItem>
            <ToggleGroupItem value="Campaign" aria-label="Toggle Campaign" className='w-2/3 checkbox font-normal data-[state=on]:font-semibold text-start justify-start font-poppins'>
              Campaign
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div className='flex flex-col gap-2'>
          <p className='font-poppins font-light'>Date</p>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="date"
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon />
                {date?.from ? (
                  date.to ? (
                    <>
                      {format(date.from, "MMM d")} -{" "}
                      {format(date.to, "MMM d")}
                    </>
                  ) : (
                    format(date.from, "LLL dd, y")
                  )
                ) : (
                  <span>Pick a date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                onSelect={setDateFilter}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className='w-full flex flex-col gap-2'>
          <p className='font-poppins font-light pb-3'>Category</p>
          <div className='w-full flex flex-col gap-4'>
            {categories.slice(0, 6).map(cat => (
              <div className="flex items-center space-x-2 w-full">
                <Checkbox id={cat} checked={filter.event_category.includes(cat)} onCheckedChange={() => {
                  setFilter(prev => {
                    const categoriesFilter = prev.event_category
                    if (categoriesFilter.includes(cat)) {
                      const foundIndex = categoriesFilter.findIndex(fil => fil === cat)
                      categoriesFilter.splice(foundIndex, 1)
                    } else {
                      categoriesFilter.push(cat)
                    }
                    return { ...prev, event_category: categoriesFilter }
                  })
                }} />
                <label
                  className="text-sm font-poppins leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {cat}
                </label>
              </div>
            ))}
            {categories.length >= 7 && <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className='w-full'>+{categories.length-6} More</Button>
              </PopoverTrigger>
              <PopoverContent side='right' align='end' className="w-[40vw] h-fit">
                <div className='w-full grid grid-rows-4 grid-cols-2 gap-4'>
                  {categories.slice(6, categories.length).map(cat => (
                    <div className="flex items-center space-x-2">
                      <Checkbox id={cat} checked={filter.event_category.includes(cat)} onCheckedChange={() => {
                        setFilter(prev => {
                          const categoriesFilter = prev.event_category
                          if (categoriesFilter.includes(cat)) {
                            const foundIndex = categoriesFilter.findIndex(fil => fil === cat)
                            categoriesFilter.splice(foundIndex, 1)
                          } else {
                            categoriesFilter.push(cat)
                          }
                          return { ...prev, event_category: categoriesFilter }
                        })
                      }} />
                      <label
                        className="text-sm font-poppins leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {cat}
                      </label>
                    </div>

                  ))}
                </div>
              </PopoverContent>
            </Popover> }
          </div>
        </div>
      </div>
    </div>
  )
}

export default EventFilter;
